/*
 * Hazelicious - hero hazelnut physics
 *
 * Hazelnuts shake loose from above the hero while the cursor moves inside it,
 * fall under gravity, bounce off hand-placed colliders sitting over the jars and
 * cartons in the hero image, and pile up on the floor at the bottom of the band.
 * The pile then drains: one at a time, resting nuts slip through the floor and
 * fall on down the page, where the nearest jar mouth attracts them like a magnet
 * and swallows them. Strength is the "jar magnet" setting; at zero there is no
 * attraction at all and only nuts already sitting above a card are released, so
 * nothing is thrown away falling past the products.
 *
 * WHY MATTER.JS
 * The nuts collide with EACH OTHER, which is what makes the floor read as a heap
 * rather than a flat scatter. That is the whole reason for the extra ~26KB; a
 * hand-rolled integrator gets bounce and roll but not stacking.
 *
 * WHY THE HERO COLLIDERS ARE HAND-PLACED
 * The hero artwork is a single flat image - there are no separate jar or carton
 * elements to hit. Collider boxes are authored in the Theme Editor as blocks on
 * the Hero section and positioned as PERCENTAGES OF THE HERO IMAGE's box, so they
 * scale with the image and survive responsive reflow. They do NOT survive the
 * image being swapped for a different composition - turn on "Show collider
 * outlines" in the section settings and drag them back into place if that happens.
 *
 * WHY THE PRODUCT JARS ARE NOT
 * Down in the products section the jars ARE real elements, marked with
 * data-hz-nut-target. Their positions are read from the DOM every layout and on
 * carousel scroll, so they need no authoring and they track the cards as they
 * slide sideways.
 *
 * WHY NUTS ARE ABSORBED RATHER THAN LANDING ON THE JARS
 * A nut cannot actually go inside a flat product photo. Shrinking and fading it
 * out at the jar mouth reads as "dropped in", and - unlike resting a nut on the
 * lid - it leaves nothing behind to be stranded when the carousel is scrolled.
 *
 * The canvas is anchored to the hero but sized to reach past the product cards,
 * so hero and products share one physics world. The hero must therefore NOT clip
 * its overflow. Nothing is drawn outside the canvas box, so no clipping is needed.
 *
 * Config arrives via data-* attributes on the canvas; see snippets/hero-nuts.liquid.
 */
(function () {
  'use strict';

  /* Inlined rather than loaded from assets/hazelnut.svg: that file has no width or
     height attributes, and browsers disagree about the intrinsic size of such an
     SVG when it is drawn to a canvas. This copy carries an explicit size. It is
     built into a data: URI and never enters the document, so its ids and fills
     cannot leak into the page. */
  var SPRITE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="144" height="144" viewBox="0 0 72 72.2">' +
    '<defs><clipPath id="c"><path d="M69.9,19.4c5.4,13.5-.2,32.7-12.7,45-3.3,3.2-3.6,6.6-6,7.6-2.1.9-5.6-1.3-9.7-1.2-17.6.6-34.2-9.1-39.7-22.8-6.2-15.4,3.9-30.8,24-39.3C49.4-1.2,63.7,4,69.9,19.4Z"/></clipPath></defs>' +
    '<path fill="#b9816a" d="M69.9,19.4c5.4,13.5-.2,32.7-12.7,45-3.3,3.2-3.6,6.6-6,7.6-2.1.9-5.6-1.3-9.7-1.2-17.6.6-34.2-9.1-39.7-22.8-6.2-15.4,3.9-30.8,24-39.3C49.4-1.2,63.7,4,69.9,19.4Z"/>' +
    '<g clip-path="url(#c)"><ellipse fill="#eebc96" cx="26.8" cy="11.7" rx="26.7" ry="6.4" transform="translate(-2.4 11.1) rotate(-22.3)"/></g>' +
    '</svg>';

  var STEP = 1000 / 60;   // fixed physics step; Matter is unstable on variable dt
  var FADE_MS = 450;      // retired nuts dissolve over this long
  var ABSORB_MS = 260;    // how long a nut takes to disappear into a jar

  /* Where the mouth of a product jar sits inside its card's image box, as
     fractions of that box.

     The card art is an <a> at 75% of the box, centred, with background
     center/contain. A jar photo is taller than it is wide, so it fills that 75%
     vertically: the jar top lands around 12% down the box and the lid runs to
     about 25%. The band below is deliberately generous - a taller target catches
     nuts reliably and, since they vanish on contact, nobody can tell where the
     boundary was. Turn on the collider overlay to see these drawn in green. */
  var MOUTH_Y = 0.22, MOUTH_W = 0.44, MOUTH_H = 0.26;

  /* Collision categories. A draining nut is excused from the hero floor and from
     the pile it just left, so it drops away cleanly instead of dragging its
     neighbours down - but it still hits the side walls and other draining nuts,
     so a stream falling into the same jar jostles instead of overlapping. */
  var CAT_NUT = 0x0001, CAT_FLOOR = 0x0002, CAT_WALL = 0x0004, CAT_DRAIN = 0x0008;

  /* The jar magnet, at strength 100%.
   *
   * This applies a FORCE toward the jar and lets the integrator do the rest. An
   * earlier version set the horizontal velocity directly, which pulled the nut
   * along as if it were on a rail - it ignored the nut's own momentum and looked
   * wrong. As a force, the nut curves in, speeds up as it closes, and can drift
   * past and swing back, which is what makes it read as attraction.
   *
   * MAGNET_G is in multiples of gravity at zero distance; pull falls off linearly
   * to nothing at MAGNET_RANGE. Because it is scaled by mass, big and small nuts
   * accelerate alike, exactly as they do under gravity.
   *
   * MAGNET_DAMP resists sideways velocity, and it is what stops the magnet acting
   * as a slingshot. Without it a nut released far from its jar arrives with so much
   * lateral speed that it sails straight over the mouth and past several cards.
   * It is set high - near critical damping - because at 0.25 the approach was
   * under-damped and nuts oscillated ACROSS the mouth without ever being inside it:
   * every remaining miss was a nut starting under 120px from its jar and swinging
   * past. Heavy damping normally costs reach, but the capture brake below removes
   * the time pressure, so it is free here.
   *
   * The downward half of the pull is squared by how well the nut is already lined
   * up. A nut off to one side is drawn mostly sideways; only once it is over the
   * jar does the magnet start hauling it down. Pulling straight at the jar instead
   * makes it fall faster the further away it is, which is backwards - it needs
   * MORE fall time to close a big lateral gap, not less.
   *
   * CAPTURE is what closes the last stragglers. A nut released out toward the page
   * edge runs out of FALL before it runs out of pull: it is still sliding sideways
   * when it reaches the mouth's height, so it passes underneath and is binned. Once
   * within CAPTURE_BAND of its jar, a nut that is not yet lined up is braked: the
   * upward force ramps to CAPTURE_BRAKE gravities at the mouth, which is over 1, so
   * it does not merely slow the nut but stops it and holds it level while the
   * lateral pull finishes sliding it in. Because that pull is a damped system it
   * always converges, so arresting the fall is what turns "most make it" into
   * "they all make it". A nut arriving already aligned is never braked and just
   * drops straight in.
   *
   * A damped SPRING onto the mouth line was tried here first and was worse (87%
   * against 92%): unbounded it flung nuts that were already below their jar, and
   * clamping it just made it a noisier brake.
   *
   * These numbers are sized for the WIDEST case, not the average one. On a 1920
   * viewport the hero runs the full width but the product row is a centred track
   * only ~1080 wide, so a nut resting at the very edge of the pile is ~500px from
   * the nearest visible jar - while the drop from hero floor to jar mouth is only
   * ~250px. Lateral speed tops out at MAGNET_G / MAGNET_DAMP, so that ratio is what
   * decides whether an edge nut can cross in time, and CAPTURE_BRAKE has to exceed
   * 1 gravity by enough to actually stop a nut that arrives already falling fast -
   * at 1.4, scaled by an 80% magnet setting, it only managed 0.12g of net lift and
   * edge nuts sank past the card before they lined up. */
  var MAGNET_G = 9, MAGNET_RANGE = 1400, MAGNET_DAMP = 0.55;
  var CAPTURE_BAND = 260, CAPTURE_BRAKE = 2.5;

  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  function init(canvas) {
    var M = window.Matter;
    var section = canvas.parentNode;
    var ctx = canvas.getContext('2d');
    if (!ctx || !section) return;

    /* The snippet also hides the canvas via CSS for both of these, but bail here
       too - otherwise we would build a world and run collision detection for a
       surface nobody can see. */
    if (window.matchMedia) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Spawning is driven by cursor travel, so a device without a fine pointer
      // has nothing to drive it.
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    }

    var cfg = {
      max:      num(canvas.dataset.max, 40),
      sens:     num(canvas.dataset.sensitivity, 160),
      gravity:  num(canvas.dataset.gravity, 1.1),
      floor:    num(canvas.dataset.floor, 0),
      drain:    canvas.dataset.drain !== 'false',
      drainMs:  num(canvas.dataset.drainInterval, 900),
      magnet:   num(canvas.dataset.magnet, 80) / 100,
      intro:    canvas.dataset.intro !== 'false',
      introN:   num(canvas.dataset.introCount, 12),
      debug:    canvas.dataset.debug === 'true'
    };

    var colliders = [];
    try { colliders = JSON.parse(canvas.dataset.colliders || '[]'); } catch (e) { colliders = []; }

    var anchor = canvas.dataset.anchor ? document.getElementById(canvas.dataset.anchor) : null;

    var sprite = new Image();
    var spriteReady = false;
    sprite.onload = function () { spriteReady = true; };
    sprite.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(SPRITE_SVG);

    var engine = M.Engine.create({ enableSleeping: true });
    engine.gravity.y = cfg.gravity;

    var nuts = [];        // { body, r, alpha, retiredAt, draining, absorbAt, targetX, fx, fy, fa }
    var statics = [];
    var zones = [];       // jar mouths, in canvas coordinates
    var W = 0, H = 0, heroH = 0, dpr = 1;
    var raf = 0, lastNow = 0, accumulator = 0, drainTimer = 0;
    var travel = 0, prevX = null, prevY = null, cursorX = 0;
    var onScreen = true;
    var prevDirty = null;

    /* ---- layout ------------------------------------------------------------ */

    /* Hero colliders are authored against the hero image, so they are measured
       relative to that box when one exists and against the hero band when it does
       not (the image picker can be left empty). */
    function anchorBox() {
      if (!anchor) return { x: 0, y: 0, w: W, h: heroH };
      var a = anchor.getBoundingClientRect();
      var c = canvas.getBoundingClientRect();
      if (!a.width || !a.height) return { x: 0, y: 0, w: W, h: heroH };
      return { x: a.left - c.left, y: a.top - c.top, w: a.width, h: a.height };
    }

    /* Jar mouths, read fresh from the DOM so they follow the carousel.
     *
     * Only jars the visitor can actually SEE are eligible. The products track is a
     * horizontal scroller, so cards beyond the right-hand edge are still in the DOM
     * with real coordinates - aiming at those sends nuts sailing off to be
     * swallowed by a jar that is not on screen, which just looks like they vanish.
     * A card must be at least 85% inside the scroller's visible box to count, so a
     * half-scrolled card does not become a target either. */
    function isOnScreen(el, r) {
      var clip = el.closest('[class*="home-products-grid-"]');
      if (!clip) return true;
      var c = clip.getBoundingClientRect();
      var shown = Math.min(r.right, c.right) - Math.max(r.left, c.left);
      return shown >= r.width * 0.85;
    }

    function readZones(heroTop) {
      var out = [];
      if (!cfg.drain) return out;
      var els = document.querySelectorAll('[data-hz-nut-target]');
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        if (!r.width || !r.height) continue;
        if (!isOnScreen(els[i], r)) continue;
        out.push({
          x: r.left + r.width / 2,
          y: r.top - heroTop + r.height * MOUTH_Y,
          w: r.width * MOUTH_W,
          h: r.height * MOUTH_H,
          halfW: r.width / 2,
          bottom: r.bottom - heroTop
        });
      }
      return out;
    }

    function layout() {
      var heroRect = section.getBoundingClientRect();
      if (!heroRect.width || !heroRect.height) return;

      W = heroRect.width;
      heroH = heroRect.height;

      zones = readZones(heroRect.top);

      // The world has to reach past the lowest jar, or a draining nut would leave
      // the canvas before it ever got there.
      var bottom = heroH;
      for (var z = 0; z < zones.length; z++) bottom = Math.max(bottom, zones[z].bottom + 40);
      H = bottom;

      canvas.style.height = H + 'px';
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      prevDirty = null;

      M.Composite.remove(engine.world, statics);
      statics = [];

      var floorY = heroH - (cfg.floor / 100) * heroH;
      var surface = { isStatic: true, friction: 0.5, restitution: 0.32 };

      // Slabs are deliberately oversized and pushed outside the frame so a fast
      // nut cannot tunnel through a thin wall between two physics steps.
      var floor = M.Bodies.rectangle(W / 2, floorY + 60, W * 3, 120, surface);
      floor.collisionFilter.category = CAT_FLOOR;
      statics.push(floor);

      var left = M.Bodies.rectangle(-60, H / 2, 120, H * 4, surface);
      var right = M.Bodies.rectangle(W + 60, H / 2, 120, H * 4, surface);
      left.collisionFilter.category = CAT_WALL;
      right.collisionFilter.category = CAT_WALL;
      statics.push(left, right);

      var box = anchorBox();
      for (var i = 0; i < colliders.length; i++) {
        var c = colliders[i];
        var cw = (num(c.w, 10) / 100) * box.w;
        var ch = (num(c.h, 5) / 100) * box.h;
        if (cw < 1 || ch < 1) continue;
        var body = M.Bodies.rectangle(
          box.x + (num(c.x, 50) / 100) * box.w,
          box.y + (num(c.y, 50) / 100) * box.h,
          cw, ch,
          { isStatic: true, angle: num(c.angle, 0) * Math.PI / 180, friction: 0.45, restitution: 0.3 }
        );
        body.collisionFilter.category = CAT_FLOOR;
        statics.push(body);
      }

      M.Composite.add(engine.world, statics);
      wake();
    }

    /* Carousel scroll moves the jars; the mouths must follow. Cheap - zones are
       plain rectangles, not bodies, so nothing is rebuilt in the physics world. */
    function refreshZones() {
      if (!cfg.drain) return;
      zones = readZones(section.getBoundingClientRect().top);
    }

    /* ---- spawning ---------------------------------------------------------- */

    function spawn() {
      var r = 9 + Math.random() * 8;
      // Biased toward the cursor rather than uniform across the width, so the
      // nuts read as being shaken loose from where your hand is.
      var x = cursorX + (Math.random() * 2 - 1) * W * 0.22;
      x = Math.max(r + 2, Math.min(W - r - 2, x));

      var body = M.Bodies.polygon(x, -r * 2 - Math.random() * 40, 8, r, {
        restitution: 0.42,
        friction: 0.32,
        frictionAir: 0.008,
        density: 0.0014,
        sleepThreshold: 40
      });
      body.collisionFilter.category = CAT_NUT;
      M.Body.setVelocity(body, { x: (Math.random() * 2 - 1) * 1.2, y: 0 });
      M.Body.setAngularVelocity(body, (Math.random() * 2 - 1) * 0.18);

      M.Composite.add(engine.world, body);
      nuts.push({ body: body, r: r, alpha: 1, retiredAt: 0, draining: false, absorbAt: 0, targetX: null });
      scheduleDrain();

      // Over the cap, retire the oldest nut that is not already on its way out.
      if (nuts.length > cfg.max) {
        for (var i = 0; i < nuts.length; i++) {
          if (!nuts[i].retiredAt && !nuts[i].draining) { nuts[i].retiredAt = performance.now(); break; }
        }
      }
      wake();
    }

    function onMove(e) {
      if (!onScreen) return;
      var rect = canvas.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var y = e.clientY - rect.top;
      cursorX = x;

      if (prevX !== null) {
        travel += Math.sqrt((x - prevX) * (x - prevX) + (y - prevY) * (y - prevY));
      }
      prevX = x;
      prevY = y;

      // At most two per event, so a single huge jump (tab-back, pointer warp)
      // cannot dump a whole handful at once.
      var budget = 2;
      while (travel >= cfg.sens && budget-- > 0) {
        travel -= cfg.sens;
        spawn();
      }
      if (travel > cfg.sens) travel = cfg.sens;
    }

    function onLeave() { prevX = null; prevY = null; }

    /* ---- draining ---------------------------------------------------------- */

    function nearestZone(x) {
      var best = null, bestD = Infinity;
      for (var j = 0; j < zones.length; j++) {
        var d = Math.abs(zones[j].x - x);
        if (d < bestD) { bestD = d; best = zones[j]; }
      }
      return best;
    }

    /* The jar a nut is committed to, resolved fresh each frame by remembered x.
       Zone objects are rebuilt on every layout and carousel scroll, so a stored
       reference would go stale; an x survives both. Committing at release also
       stops a nut being handed from jar to jar as it falls, which was what let it
       weave between cards and reach none of them. */
    function targetZone(n) {
      if (n.targetX == null) return null;
      var best = null, bestD = Infinity;
      for (var j = 0; j < zones.length; j++) {
        var d = Math.abs(zones[j].x - n.targetX);
        if (d < bestD) { bestD = d; best = zones[j]; }
      }
      return best;
    }

    /* Pick the jar a nut should aim for: the nearest visible one that is below it.
       Chosen on lateral distance, since vertical distance is much the same for
       every card in the row. */
    function pickTarget(p) {
      var best = null, bestD = Infinity;
      for (var j = 0; j < zones.length; j++) {
        var z = zones[j];
        if (z.y <= p.y) continue;
        var d = Math.abs(z.x - p.x);
        if (d < bestD) { bestD = d; best = z; }
      }
      return best;
    }

    /* Release one resting nut. With the magnet on, any resting nut will do - it
       will be drawn across to the nearest card on the way down. With the magnet at
       zero nothing pulls it sideways, so only a nut already sitting above a card is
       eligible; anything else would just fall past the products and be binned. */
    function doDrain() {
      if (!cfg.drain || !zones.length) return false;

      for (var i = 0; i < nuts.length; i++) {
        var n = nuts[i];
        if (n.draining || n.retiredAt || !n.body.isSleeping) continue;

        if (cfg.magnet <= 0) {
          var above = false;
          for (var j = 0; j < zones.length; j++) {
            if (Math.abs(n.body.position.x - zones[j].x) <= zones[j].w / 2) { above = true; break; }
          }
          if (!above) continue;
        }

        // Commit to a jar now and stay committed. No jar below it at all means
        // there is nowhere for it to go, so leave it in the pile rather than
        // dropping it off the bottom of the page.
        var t = pickTarget(n.body.position);
        if (!t) continue;
        n.targetX = t.x;

        n.draining = true;
        // Leaves the pile and the floor behind, but still meets the walls and any
        // other nut already on its way down.
        n.body.collisionFilter.category = CAT_DRAIN;
        n.body.collisionFilter.mask = CAT_WALL | CAT_DRAIN;
        M.Sleeping.set(n.body, false);
        return true;
      }
      return false;
    }

    /* The drip runs on its own timer rather than inside the render loop. With the
       magnet on, every resting nut is drainable, so a loop-side check would report
       "not settled" forever and rAF would never stop between drips. */
    function scheduleDrain() {
      if (drainTimer || !cfg.drain || !onScreen || !nuts.length) return;
      drainTimer = setTimeout(function () {
        drainTimer = 0;
        if (doDrain()) wake();
        scheduleDrain();
      }, cfg.drainMs);
    }

    /* Pull a falling nut toward the nearest jar mouth. Only once it is clear of
       the hero floor line, so it never appears to creep across the top of the pile
       it just left. */
    function magnetise(n) {
      if (cfg.magnet <= 0 || !n.draining || n.absorbAt) return;
      var p = n.body.position;
      if (p.y < heroH - (cfg.floor / 100) * heroH) return;

      var z = targetZone(n);
      if (!z) return;

      var dx = z.x - p.x;
      var dy = z.y - p.y;
      var d = Math.sqrt(dx * dx + dy * dy);
      if (d < 1 || d > MAGNET_RANGE) return;

      // Linear falloff: barely there at the far edge of range, strongest on
      // approach, so the nut drifts at first and commits near the jar.
      var close = 1 - d / MAGNET_RANGE;
      // Matter applies gravity as mass * gravity * scale; matching that here keeps
      // the magnet expressed honestly in gravities.
      var unit = n.body.mass * engine.gravity.scale * cfg.magnet;
      var f = close * MAGNET_G * unit;

      var alignment = dy / d;   // 1 when the jar is straight below, 0 when beside
      var fy = alignment * alignment * f;

      /* Near the mouth but not lined up: hang it at mouth height while it finishes
         sliding across, rather than letting it drop past and be binned. */
      // Held from CAPTURE_BAND above the mouth all the way down to the foot of the
      // card. Stopping at the mouth line let a nut with a long way still to slide
      // sink below the card before it lined up, and once out of the card there is
      // nothing left to catch it.
      if (p.y <= z.bottom && dy < CAPTURE_BAND && Math.abs(dx) > z.w * 0.35) {
        // Ramps to full at the mouth and stays there for the short distance just
        // below it, so a nut arriving with downward momentum is stopped rather
        // than merely slowed, and hangs level while it finishes sliding in.
        var urgency = 1 - Math.max(dy, 0) / CAPTURE_BAND;
        fy -= n.body.mass * engine.gravity.scale * engine.gravity.y
              * CAPTURE_BRAKE * urgency * cfg.magnet;
      }

      M.Body.applyForce(n.body, p, {
        x: (dx / d) * f - n.body.velocity.x * MAGNET_DAMP * close * unit,
        y: fy
      });
    }

    /* Swallowed by the jar: pull the body out of the world and hand the nut over
       to the renderer to shrink and fade on the spot. */
    function tryAbsorb(n, now) {
      if (!n.draining || n.absorbAt) return;
      var p = n.body.position;
      for (var j = 0; j < zones.length; j++) {
        var z = zones[j];
        /* Lined up, and level with the mouth or anywhere below it down to the foot
           of the card. A narrow band around the mouth line was not enough: nuts
           converging on the same jar jostle each other, and one knocked below the
           band could never be caught again even though it was dead on target - it
           just tracked the jar's x all the way off the page. Anywhere inside the
           card still reads as going into the jar, because the jar fills the card. */
        if (Math.abs(p.x - z.x) <= z.w / 2 &&
            p.y >= z.y - z.h / 2 && p.y <= z.bottom) {
          swallow(n, now, p);
          return;
        }
      }

      /* Last chance. A nut knocked sideways by the crowd around a busy jar can
         leave the bottom of its own card still only a few tens of pixels off
         centre; without this it falls the rest of the page and is binned, which is
         exactly the "some just go down" the checks above are meant to eliminate.
         Bounded by the card's own half-width, so the nut always disappears within
         the card's footprint. This used the MOUTH half-width before, which is only
         a fifth of the card - edge nuts were ending 100-145px out and sailing
         straight through the net that was supposed to catch them. */
      var t = targetZone(n);
      if (t && p.y > t.bottom && Math.abs(p.x - t.x) <= t.halfW) swallow(n, now, p);
    }

    function swallow(n, now, p) {
      n.absorbAt = now;
      n.fx = p.x;
      n.fy = p.y;
      n.fa = n.body.angle;
      M.Composite.remove(engine.world, n.body);
    }

    /* ---- loop -------------------------------------------------------------- */

    // Everything asleep and nothing in transit means the canvas already shows the
    // final frame, so the loop can stop entirely instead of idling at 60fps.
    function settled() {
      for (var i = 0; i < nuts.length; i++) {
        var n = nuts[i];
        if (n.retiredAt || n.draining || n.absorbAt || !n.body.isSleeping) return false;
      }
      // Nuts still owed a drip do not count as unsettled - scheduleDrain's timer
      // restarts the loop when the next one is due.
      return true;
    }

    function wake() {
      if (!raf && onScreen) {
        lastNow = 0;
        accumulator = 0;
        raf = requestAnimationFrame(frame);
      }
    }

    function frame(now) {
      raf = 0;

      accumulator += lastNow ? Math.min(now - lastNow, 100) : STEP;
      lastNow = now;
      while (accumulator >= STEP) {
        /* Both of these belong INSIDE the step loop, not once per frame.
           Matter consumes applied forces on every Engine.update, so a magnet
           applied per frame only acted on the first step of that frame. And a nut
           falling ~15px per step can cross the whole 50-odd-pixel mouth between
           two frames, so testing absorption per frame let dead-on nuts tunnel
           straight through the jar - every remaining miss was one of those. */
        for (var s = 0; s < nuts.length; s++) {
          if (!nuts[s].absorbAt) magnetise(nuts[s]);
        }
        M.Engine.update(engine, STEP);
        accumulator -= STEP;
        for (var a = 0; a < nuts.length; a++) {
          if (!nuts[a].absorbAt) tryAbsorb(nuts[a], now);
        }
      }

      for (var i = nuts.length - 1; i >= 0; i--) {
        var n = nuts[i];

        if (n.absorbAt) {
          if (now - n.absorbAt >= ABSORB_MS) { nuts.splice(i, 1); }
          continue;
        }


        if (n.retiredAt) {
          n.alpha = 1 - (now - n.retiredAt) / FADE_MS;
          if (n.alpha <= 0) {
            M.Composite.remove(engine.world, n.body);
            nuts.splice(i, 1);
            continue;
          }
        }
        // Anything that escaped the frame (a wedged nut squeezed out by the pile,
        // or a drained one that missed every jar) is dropped rather than
        // simulated forever off-screen.
        if (n.body.position.y > H + 200) {
          M.Composite.remove(engine.world, n.body);
          nuts.splice(i, 1);
        }
      }

      draw();
      if (!settled()) raf = requestAnimationFrame(frame);
    }

    /* ---- drawing ----------------------------------------------------------- */

    // The canvas now spans the hero AND the products below, which is far too much
    // area to clear wholesale every frame. Clear only where nuts were and are.
    function dirtyRect() {
      if (!nuts.length) return null;
      var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (var i = 0; i < nuts.length; i++) {
        var n = nuts[i];
        var p = n.absorbAt ? { x: n.fx, y: n.fy } : n.body.position;
        var pad = n.r * 2;
        if (p.x - pad < x0) x0 = p.x - pad;
        if (p.y - pad < y0) y0 = p.y - pad;
        if (p.x + pad > x1) x1 = p.x + pad;
        if (p.y + pad > y1) y1 = p.y + pad;
      }
      return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    }

    function draw() {
      var cur = dirtyRect();
      // Union with last frame's box so pixels a nut has just vacated are erased.
      var clr = cur;
      if (prevDirty && cur) {
        var x0 = Math.min(cur.x, prevDirty.x), y0 = Math.min(cur.y, prevDirty.y);
        var x1 = Math.max(cur.x + cur.w, prevDirty.x + prevDirty.w);
        var y1 = Math.max(cur.y + cur.h, prevDirty.y + prevDirty.h);
        clr = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
      } else if (!cur) {
        clr = prevDirty;
      }
      if (clr) ctx.clearRect(clr.x - 2, clr.y - 2, clr.w + 4, clr.h + 4);
      else ctx.clearRect(0, 0, W, H);
      prevDirty = cur;

      if (spriteReady) {
        for (var i = 0; i < nuts.length; i++) {
          var n = nuts[i];
          var alpha = n.alpha, scale = 1, x, y, angle;

          if (n.absorbAt) {
            var t = Math.min((performance.now() - n.absorbAt) / ABSORB_MS, 1);
            scale = 1 - t;
            alpha = 1 - t;
            x = n.fx;
            y = n.fy + t * 14;   // keeps sinking as it shrinks
            angle = n.fa;
          } else {
            x = n.body.position.x;
            y = n.body.position.y;
            angle = n.body.angle;
          }
          if (scale <= 0 || alpha <= 0) continue;

          var d = n.r * 2.25 * scale;
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.translate(x, y);
          ctx.rotate(angle);
          ctx.drawImage(sprite, -d / 2, -d / 2, d, d);
          ctx.restore();
        }
      }

      if (cfg.debug) drawDebug();
    }

    function drawDebug() {
      // Debug strokes sit outside the dirty rect, so redraw the whole overlay.
      ctx.save();
      ctx.strokeStyle = '#e02b2b';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      for (var i = 0; i < statics.length; i++) {
        var v = statics[i].vertices;
        ctx.beginPath();
        ctx.moveTo(v[0].x, v[0].y);
        for (var j = 1; j < v.length; j++) ctx.lineTo(v[j].x, v[j].y);
        ctx.closePath();
        ctx.stroke();
      }
      ctx.strokeStyle = '#1b8f3a';
      for (var z = 0; z < zones.length; z++) {
        var s = zones[z];
        ctx.strokeRect(s.x - s.w / 2, s.y - s.h / 2, s.w, s.h);
      }
      ctx.restore();
    }

    /* ---- wiring ------------------------------------------------------------ */

    layout();

    /* Intro sweep: on the first page load a hand appears to pass over the hero
       from left to right, shaking a short burst of nuts loose - so a visitor who
       has not touched the mouse yet still sees what the hero does. Implemented as
       the same spawn() the real cursor drives, with cursorX walked across the
       width; one spawn every 90ms reads as a sweep rather than a dump. Starts
       ~700ms after boot so the page has settled, and only if the hero is on
       screen at that moment - reloading mid-page must not rain nuts into an
       unwatched hero. Runs once. */
    if (cfg.intro) {
      setTimeout(function () {
        if (!onScreen) return;
        var n = Math.max(1, Math.round(cfg.introN));
        for (var i = 0; i < n; i++) {
          setTimeout((function (idx) {
            return function () {
              if (!onScreen) return;
              cursorX = ((idx + 0.5) / n) * W;
              spawn();
            };
          })(i), i * 90);
        }
      }, 700);
    }

    section.addEventListener('mousemove', onMove);
    section.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', layout, { passive: true });
    // Page scroll changes where the jars are relative to the hero.
    window.addEventListener('scroll', refreshZones, { passive: true });
    if (anchor && !anchor.complete) anchor.addEventListener('load', layout);
    if ('ResizeObserver' in window) new ResizeObserver(layout).observe(section);

    // Sideways carousel movement moves the jars too.
    var tracks = document.querySelectorAll('[data-hz-nut-target]');
    for (var t = 0; t < tracks.length; t++) {
      var scroller = tracks[t].closest('[class*="home-products-grid-"]');
      if (scroller && !scroller.__hzBound) {
        scroller.__hzBound = true;
        scroller.addEventListener('scroll', refreshZones, { passive: true });
      }
    }

    if ('IntersectionObserver' in window) {
      // Observe the canvas, not the hero: nuts can be down among the products
      // long after the hero band itself has scrolled out of view.
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (onScreen) {
          wake();
          scheduleDrain();
        } else if (raf) {
          if (drainTimer) { clearTimeout(drainTimer); drainTimer = 0; }
          // Scrolled away: freeze rather than simulate an unwatched pile.
          cancelAnimationFrame(raf);
          raf = 0;
        }
      }, { threshold: 0 }).observe(canvas);
    }
  }

  function boot() {
    if (!window.Matter) return;
    var nodes = document.querySelectorAll('[data-hz-nuts]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
